import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { fetchProductReviews, DisplayReview } from "@/api/reviewSectionApi";
import { API } from "@/constants/api";
import { palette } from "@/styles/palette";
import { fontStyle, font } from "../../styles/font";
import logger from "@/utils/logger";

interface ReviewSectionProps {
    productAltKey: string; // 상품 고유 키만 전달받음
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ productAltKey }) => {
    const [reviews, setReviews] = useState<DisplayReview[]>([]);
    const [totalReviews, setTotalReviews] = useState<number>(0);
    const [hasMoreReviews, setHasMoreReviews] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    // 초기 리뷰 로드
    useEffect(() => {
        if (productAltKey) {
            fetchReviews(0);
        }
    }, [productAltKey]);

    // 리뷰 데이터 가져오기
    const fetchReviews = async (page: number) => {
        if (!productAltKey) return;

        try {
            setLoading(true);
            logger.log(`리뷰 데이터 요청: 페이지 ${page}, 상품 ${productAltKey}`);

            const result = await fetchProductReviews(productAltKey, page);

            if (result) {
                logger.log(
                    `리뷰 데이터 응답: 현재 페이지 ${page}, 리뷰 ${result.reviews.length}개, 총 ${result.totalCount}개, 더 있음: ${result.hasMore}`
                );

                if (page === 0) {
                    // 첫 페이지인 경우 기존 데이터 교체
                    setReviews(result.reviews);
                } else {
                    // 이후 페이지는 기존 데이터에 추가
                    setReviews((prev) => [...prev, ...result.reviews]);
                }

                // 전체 리뷰 수 업데이트
                setTotalReviews(result.totalCount);
                setHasMoreReviews(result.hasMore);
                setCurrentPage(page);
            }
        } catch (err) {
            logger.error("리뷰 데이터 로딩 중 오류 발생:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    // 리뷰 더 보기 클릭 시 다음 페이지 로드
    const handleShowMoreReviews = () => {
        if (!loading) {
            fetchReviews(currentPage + 1);
        }
    };

    return (
        <SectionWrapper id="review-section">
            <Section>
                <SectionTitle>
                    제품 평가 리뷰
                    <ReviewCount>({totalReviews.toLocaleString()}개)</ReviewCount>
                </SectionTitle>
                <ReviewList>
                    {reviews.map((review) => (
                        <ReviewItem key={review.altKey}>
                            <ReviewTop>
                                <RatingBadge>
                                    <StarIcon />
                                    <RatingText>{review.rating}</RatingText>
                                </RatingBadge>
                                <ReviewerInfo>
                                    <ReviewDate>{review.date}</ReviewDate>
                                </ReviewerInfo>
                            </ReviewTop>
                            <ReviewContentWrapper>
                                {review.images && review.images.length > 0 && (
                                    <ReviewImageWrapper>
                                        <ReviewImage
                                            src={`${API.IMAGE_DOMAIN}/${review.images[0]}`}
                                            alt="리뷰 이미지"
                                        />
                                    </ReviewImageWrapper>
                                )}
                                <ReviewContent hasImage={review.images && review.images.length > 0}>
                                    {review.content}
                                </ReviewContent>
                            </ReviewContentWrapper>
                            <Divider />
                        </ReviewItem>
                    ))}
                </ReviewList>
                {reviews.length > 0 && hasMoreReviews && (
                    <ShowMoreButton onClick={handleShowMoreReviews}>
                        리뷰 더 보기 ({reviews.length}/{totalReviews})
                    </ShowMoreButton>
                )}
                {reviews.length === 0 && !loading && (
                    <NoReviewMessage>
                        <p>아직 리뷰가 없어요.</p>
                        <p>첫 번째 평가를 남겨보세요!</p>
                    </NoReviewMessage>
                )}
                {loading && (
                    <LoadingIndicator>
                        <span>리뷰를 불러오는 중...</span>
                    </LoadingIndicator>
                )}
            </Section>
        </SectionWrapper>
    );
};

export default ReviewSection;

// 공통 텍스트 스타일
const commonTextStyles = `
    ${fontStyle.base}
    color: #1F1F1F;
`;

const SectionWrapper = styled.div`
    margin-bottom: 24px;
    width: 100%;
    padding-top: 20px;
`;

const Section = styled.section`
    margin-bottom: 24px;
    width: 100%;
    padding-top: 20px;
`;

const SectionTitle = styled.h3`
    ${commonTextStyles}
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: clamp(18px, 3.5vw, 24px);
    line-height: 1.3;
    font-weight: 600;
    margin-bottom: 16px;

    @media screen and (max-width: 480px) {
        margin-bottom: 12px;
    }
`;

const ReviewCount = styled.span`
    ${commonTextStyles}
    font-weight: 350;
    font-size: 14px;
    line-height: 16px;
    color: ${palette.gray[600]};
    display: flex;
    align-items: center;
`;

const ReviewList = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const ReviewItem = styled.div`
    padding: 20px 0;
`;

const ReviewTop = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 13px;
`;

const RatingBadge = styled.div`
    display: flex;
    align-items: center;
    padding: 5px 9px;
    gap: 3px;
    height: 20px;
    background: #ffc700;
    border-radius: 15px;
`;

const StarIcon = styled.div`
    width: 16px;
    height: 16px;
    background: url("/icon/green-star.png") no-repeat center;
    background-size: contain;
`;

const RatingText = styled.span`
    ${commonTextStyles}
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
    color: #ffffff;
`;

const ReviewerInfo = styled.div`
    display: flex;
    align-items: center;
    margin-left: 15px;
    gap: 10px;
`;

const ReviewDate = styled.span`
    ${commonTextStyles}
    font-weight: 350;
    font-size: 12px;
    line-height: 14px;
    color: ${palette.gray[600]};
`;

const ReviewContentWrapper = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
`;

const ReviewImageWrapper = styled.div`
    flex-shrink: 0;
`;

const ReviewImage = styled.img`
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
`;

const ReviewContent = styled.p<{ hasImage?: boolean }>`
    ${commonTextStyles}
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: ${palette.gray[900]};
    margin: 0;
    white-space: pre-line;
    flex: 1;
`;

const Divider = styled.div`
    width: 100%;
    height: 1px;
    background: ${palette.gray[200]};
    margin-top: 16px;
`;

const ShowMoreButton = styled.button`
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${palette.white};
    border: 1px solid ${palette.gray[300]};
    border-radius: 8px;
    margin-top: 16px;
    ${font(500, "14px", 1.15)}
    color: ${palette.gray[700]};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${palette.gray[100]};
    }
`;

const NoReviewMessage = styled.div`
    width: 100%;
    padding: 40px 20px;
    text-align: center;
    background-color: ${palette.gray[100]};
    border-radius: 8px;

    p {
        ${commonTextStyles}
        font-size: 16px;
        color: ${palette.gray[700]};
        margin-bottom: 8px;

        &:last-child {
            margin-bottom: 0;
            color: ${palette.gray[500]};
            font-size: 14px;
        }
    }
`;

const LoadingIndicator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;

    span {
        ${commonTextStyles}
        font-size: 14px;
        color: ${palette.gray[600]};
    }
`;
